import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { Building2, CheckCircle, AlertCircle, ChevronDown, ChevronRight, Landmark, Banknote, Briefcase } from 'lucide-react';

function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div className={`fixed bottom-6 right-6 z-[999] flex items-center gap-3 px-5 py-3 rounded-2xl shadow-modal text-white text-sm font-medium`}
      style={{ background: ok ? 'linear-gradient(135deg,#059669,#047857)' : 'linear-gradient(135deg,#dc2626,#b91c1c)' }}>
      {ok ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {msg}
    </div>
  );
}

const CompanySettings: React.FC = () => {
  const { companyId } = useAuth();
  const { company, loading, updateCompany } = useCompanySettings(companyId);
  const [form, setForm] = useState({
    name: '', org_number: '', address: '', postal_code: '', city: '', phone: '', email: '',
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  // Future authority integrations — collapsed by default (same as P-ivilotid + DG-plånbok).
  const [showGov, setShowGov] = useState(false);

  useEffect(() => {
    if (company) {
      setForm({
        name: company.name ?? '',
        org_number: company.org_number ?? '',
        address: company.address ?? '',
        postal_code: company.postal_code ?? '',
        city: company.city ?? '',
        phone: company.phone ?? '',
        email: company.email ?? '',
      });
    }
  }, [company?.id]);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await updateCompany({
      name: form.name.trim(),
      org_number: form.org_number.trim() || null,
      address: form.address.trim() || null,
      postal_code: form.postal_code.trim() || null,
      city: form.city.trim() || null,
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
    });
    setSaving(false);
    if (error) showToast(error, false);
    else showToast('Company profile updated');
  };

  if (!companyId) {
    return (
      <Layout title="Company" breadcrumbs={[{ label: 'Company settings' }]}>
        <div className="card p-8 text-center text-slate-500">No company linked to your account.</div>
      </Layout>
    );
  }

  return (
    <Layout title="My Company" breadcrumbs={[{ label: 'Home', path: '/dashboard' }, { label: 'Company' }]}>
      <div className="max-w-2xl">
        <div className="mb-6 flex items-center gap-3">
          <Building2 className="w-8 h-8 text-electric-500" />
          <div>
            <h2 className="text-lg font-bold text-slate-800">Company profile</h2>
            <p className="text-sm text-slate-400">Shown to drivers in DG-plånbok and P-ivilotid inspection</p>
          </div>
        </div>

        {loading ? (
          <div className="card p-8 skeleton h-40" />
        ) : (
          <div className="card p-6 space-y-4">
            {company?.status === 'suspended' && (
              <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                This company is suspended. Drivers cannot log in until reactivated by government admin.
              </div>
            )}
            {[
              { label: 'Company name', key: 'name' as const },
              { label: 'Org.nr', key: 'org_number' as const },
              { label: 'Address', key: 'address' as const },
              { label: 'Postal code', key: 'postal_code' as const },
              { label: 'City', key: 'city' as const },
              { label: 'Phone', key: 'phone' as const },
              { label: 'Email', key: 'email' as const, type: 'email' },
            ].map((f) => (
              <div key={f.key}>
                <label className="block text-sm font-medium text-slate-600 mb-1">{f.label}</label>
                <input
                  type={f.type ?? 'text'}
                  className="input-premium"
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                />
              </div>
            ))}
            <button onClick={handleSave} disabled={saving} className="btn-primary px-6 py-2.5 disabled:opacity-60">
              {saving ? 'Saving…' : 'Save company'}
            </button>
          </div>
        )}

        {/* Future authority integrations — collapsible, collapsed by default */}
        <div className="mt-6">
          <button
            onClick={() => setShowGov((v) => !v)}
            className="w-full flex items-center justify-between card px-5 py-4 text-left"
          >
            <div>
              <h3 className="text-sm font-bold text-slate-800">Myndighetsdata</h3>
              <p className="text-xs text-slate-400 mt-0.5">Kommande integrationer — verifierade externa källor</p>
            </div>
            {showGov ? (
              <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />
            ) : (
              <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
            )}
          </button>
          {showGov && (
            <div className="mt-3 space-y-3">
              {[
                { icon: <Landmark className="w-5 h-5 text-slate-400" />, title: 'Transportstyrelsen', desc: 'Taxiförarlegitimation, taxitrafiktillstånd och fordonsstatus' },
                { icon: <Banknote className="w-5 h-5 text-slate-400" />, title: 'Skatteverket', desc: 'F-skatt, momsregistrering och deklarerad inkomst' },
                { icon: <Briefcase className="w-5 h-5 text-slate-400" />, title: 'Arbetsförmedlingen', desc: 'Anställningsstöd / bidrag kopplat till föraren' },
              ].map((g) => (
                <div key={g.title} className="flex items-center gap-3 card px-4 py-3 border-dashed">
                  <div className="shrink-0">{g.icon}</div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-700">{g.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{g.desc}</p>
                  </div>
                  <span className="text-[10px] font-bold text-slate-400 border border-slate-300 rounded-lg px-1.5 py-0.5 shrink-0">
                    Kommande
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {toast && <Toast msg={toast.msg} ok={toast.ok} />}
    </Layout>
  );
};

export default CompanySettings;
